import { stripServerManagedFields } from './widgets/ideaMerge';
import type { QueryClient } from '@tanstack/query-core';
import type {
    ConcreteIdea,
    Idea,
    IfThenResearchPlan,
    ScannerPlan,
    ScreenerPlan,
    WatchlistPlan,
} from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import type {
    ScannerWidgetModel,
    ScreenerWidgetModel,
    SearchAlphaWidgetModel,
    WatchlistWidgetModel,
} from '@thinkalpha/ui-types/contracts/workspace';
import { getIdeaByIdQuery } from 'src/queries/ideas';
import { getWorkspaceConfigQuery } from 'src/queries/workspace';
import {
    createDefaultScannerWidget,
    createDefaultScreenerWidget,
    createDefaultSearchAlphaWidget,
} from 'src/store/defaults/widget';
import type {
    ScannerWidgetViewModel,
    ScreenerWidgetViewModel,
    SearchAlphaWidgetViewModel,
    WatchlistWidgetViewModel,
} from 'src/store/types';

export function shouldDeferIdeaHydration<T extends { id?: string | null }>(
    source: T | string | null | undefined,
): source is T | string {
    return typeof source === 'string' ? source.length > 0 : Boolean(source?.id);
}

export function createDeferredIdeaHydration<TSource>(source: TSource) {
    return {
        status: 'deferred',
        source,
    } as const;
}

export async function loadDeferredSearchAlphaIdea(
    queryClient: QueryClient,
    incomingIdea: SearchAlphaWidgetModel['idea'],
): Promise<Pick<SearchAlphaWidgetViewModel, 'idea' | 'isIdeaDirty'>> {
    let idea: SearchAlphaWidgetViewModel['idea'] | undefined;
    let isIdeaDirty = false;

    if (typeof incomingIdea === 'string') {
        const serverIdea = await queryClient.fetchQuery(
            getIdeaByIdQuery<ConcreteIdea & { plan: IfThenResearchPlan }>(incomingIdea),
        );
        if (serverIdea) {
            idea = serverIdea;
        }
    } else if (incomingIdea?.id) {
        const serverIdea = await queryClient.fetchQuery(
            getIdeaByIdQuery<ConcreteIdea & { plan: IfThenResearchPlan }>(incomingIdea.id),
        );
        if (serverIdea) {
            idea = { ...serverIdea, ...stripServerManagedFields(incomingIdea) };
            isIdeaDirty = true;
        } else {
            const { id: _id, ...ideaWithoutId } = incomingIdea;
            idea = ideaWithoutId;
            isIdeaDirty = true;
        }
    } else if (incomingIdea) {
        idea = incomingIdea;
        isIdeaDirty = true;
    }

    return {
        idea: idea ?? createDefaultSearchAlphaWidget().idea,
        isIdeaDirty,
    };
}

export async function loadDeferredScannerIdea(
    queryClient: QueryClient,
    incomingIdea: ScannerWidgetModel['idea'],
): Promise<Pick<ScannerWidgetViewModel, 'idea' | 'isIdeaDirty'>> {
    let idea: ScannerWidgetViewModel['idea'] | undefined;
    let isIdeaDirty = false;

    if (typeof incomingIdea === 'string') {
        const serverIdea = await queryClient.fetchQuery(
            getIdeaByIdQuery<ConcreteIdea & { plan: ScannerPlan }>(incomingIdea),
        );
        if (serverIdea) {
            idea = serverIdea;
        }
    } else if (incomingIdea?.id) {
        const serverIdea = await queryClient.fetchQuery(
            getIdeaByIdQuery<ConcreteIdea & { plan: ScannerPlan }>(incomingIdea.id),
        );
        if (serverIdea) {
            idea = { ...serverIdea, ...stripServerManagedFields(incomingIdea) };
            isIdeaDirty = true;
        } else {
            const { id: _id, ...ideaWithoutId } = incomingIdea;
            idea = ideaWithoutId;
            isIdeaDirty = true;
        }
    } else if (incomingIdea) {
        idea = incomingIdea;
        isIdeaDirty = true;
    }

    return {
        idea: idea ?? createDefaultScannerWidget().idea,
        isIdeaDirty,
    };
}

export async function loadDeferredScreenerIdea(
    queryClient: QueryClient,
    incomingIdea: ScreenerWidgetModel['idea'],
): Promise<Pick<ScreenerWidgetViewModel, 'idea' | 'isIdeaDirty'>> {
    let idea: ScreenerWidgetViewModel['idea'] | undefined;
    let isIdeaDirty = false;

    if (typeof incomingIdea === 'string') {
        const serverIdea = await queryClient.fetchQuery(
            getIdeaByIdQuery<ConcreteIdea & { plan: ScreenerPlan }>(incomingIdea),
        );
        if (serverIdea) {
            idea = serverIdea;
        }
    } else if (incomingIdea?.id) {
        const serverIdea = await queryClient.fetchQuery(
            getIdeaByIdQuery<ConcreteIdea & { plan: ScreenerPlan }>(incomingIdea.id),
        );
        if (serverIdea) {
            idea = { ...serverIdea, ...stripServerManagedFields(incomingIdea) };
            isIdeaDirty = true;
        } else {
            const { id: _id, ...ideaWithoutId } = incomingIdea;
            idea = ideaWithoutId;
            isIdeaDirty = true;
        }
    } else if (incomingIdea) {
        idea = incomingIdea;
        isIdeaDirty = true;
    }

    return {
        idea: idea ?? createDefaultScreenerWidget().idea,
        isIdeaDirty,
    };
}

function createFallbackWatchlistIdea(defaultUniverseId: string | null): Idea & { plan: WatchlistPlan } {
    return {
        name: null,
        key: 'ticker',
        isTemplate: false,
        description: null,
        defaultUniverseId,
        defaultColumnTemplateId: null,
        plan: { type: 'watchlist', includeList: [] },
    };
}

function areSetsEqual<T>(left: Set<T>, right: Set<T>) {
    if (left.size !== right.size) return false;
    for (const item of left) {
        if (!right.has(item)) return false;
    }
    return true;
}

function hasLocalWatchlistMetadataEdits(
    serverIdea: Idea & { plan: WatchlistPlan },
    incomingIdea: Idea & { plan: WatchlistPlan },
) {
    const serverFields = stripServerManagedFields(serverIdea);
    const incomingFields = stripServerManagedFields(incomingIdea);

    return (
        serverFields.name !== incomingFields.name ||
        serverFields.description !== incomingFields.description ||
        serverFields.defaultUniverseId !== incomingFields.defaultUniverseId ||
        serverFields.defaultColumnTemplateId !== incomingFields.defaultColumnTemplateId ||
        serverFields.isTemplate !== incomingFields.isTemplate
    );
}

export async function loadDeferredWatchlistIdea(
    queryClient: QueryClient,
    incomingIdea: WatchlistWidgetModel['idea'],
): Promise<Pick<WatchlistWidgetViewModel, 'idea' | 'isIdeaDirty'>> {
    let idea: WatchlistWidgetViewModel['idea'];
    let isIdeaDirty = false;

    if (typeof incomingIdea === 'string') {
        const serverIdea = await queryClient.fetchQuery(
            getIdeaByIdQuery<ConcreteIdea & { plan: WatchlistPlan }>(incomingIdea),
        );
        if (serverIdea) {
            idea = serverIdea;
        } else {
            const config = await queryClient.fetchQuery(getWorkspaceConfigQuery());
            idea = createFallbackWatchlistIdea(config.defaultUniverseId);
            isIdeaDirty = true;
        }
    } else if (incomingIdea.id) {
        const serverIdea = await queryClient.fetchQuery(
            getIdeaByIdQuery<ConcreteIdea & { plan: WatchlistPlan }>(incomingIdea.id),
        );
        if (serverIdea) {
            idea = { ...serverIdea, ...stripServerManagedFields(incomingIdea) };
            const serverSymbols = new Set(serverIdea.plan.includeList);
            const localSymbols = new Set(incomingIdea.plan.includeList);
            let hasPlanEdits: boolean;
            try {
                hasPlanEdits = serverSymbols.symmetricDifference(localSymbols).size > 0;
            } catch {
                hasPlanEdits = !areSetsEqual(serverSymbols, localSymbols);
            }
            isIdeaDirty = hasPlanEdits || hasLocalWatchlistMetadataEdits(serverIdea, incomingIdea);
        } else {
            const { id: _id, ...ideaWithoutId } = incomingIdea;
            idea = ideaWithoutId;
            isIdeaDirty = incomingIdea.plan.includeList.length > 0;
        }
    } else {
        idea = incomingIdea;
        isIdeaDirty = idea.plan.includeList.length > 0;
    }

    return { idea, isIdeaDirty };
}
